import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Loader2, Activity, User, FileText, Upload, LogIn, ArrowRight, Send, UserCheck, Shuffle } from 'lucide-react';
import api from '@/utils/api';
import { showError } from '@/utils/toast';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filterType ? { entity_type: filterType } : {};
      const res = await api.get('/activities', { params });
      setActivities(res.data);
    } catch (error) {
      showError('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (entityType, action) => {
    if (action === 'login') return <LogIn size={20} />;
    if (action === 'assigned' || action === 'reassigned') return <UserCheck size={20} />;
    if (action === 'submitted_to_manager') return <Send size={20} />;
    if (action === 'status_changed') return <Shuffle size={20} />;
    if (entityType === 'lead') return <FileText size={20} />;
    if (entityType === 'document') return <Upload size={20} />;
    if (entityType === 'user') return <User size={20} />;
    return <Activity size={20} />;
  };

  const getColor = (action) => {
    switch (action) {
      case 'created': return 'bg-emerald-100 text-emerald-700';
      case 'updated': return 'bg-blue-100 text-blue-700';
      case 'deleted': return 'bg-rose-100 text-rose-700';
      case 'uploaded': return 'bg-violet-100 text-violet-700';
      case 'assigned':
      case 'reassigned': return 'bg-amber-100 text-amber-700';
      case 'submitted_to_manager': return 'bg-cyan-100 text-cyan-700';
      case 'status_changed': return 'bg-indigo-100 text-indigo-700';
      case 'login': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatRole = (role) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const filtered = activities.filter((a) => {
    if (filterAction && a.action !== filterAction) return false;
    if (search) {
      const s = search.toLowerCase();
      return [a.details, a.actor_name, a.lead_number, a.customer_name, a.actor_employee_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s));
    }
    return true;
  });

  const uniqueActions = Array.from(new Set(activities.map((a) => a.action))).sort();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Audit Log</h1>
          <p className="text-muted-foreground">Complete record of every action — assignments, status changes, edits and logins</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-3">
          <select
            data-testid="filter-activity-type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none text-sm"
          >
            <option value="">All Entities</option>
            <option value="lead">Leads</option>
            <option value="document">Documents</option>
            <option value="user">Users</option>
            <option value="followup">Follow-ups</option>
            <option value="settings">Settings</option>
            <option value="role">Roles</option>
            <option value="product">Products</option>
            <option value="lead_source">Lead Sources</option>
          </select>
          <select
            data-testid="filter-activity-action"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none text-sm"
          >
            <option value="">All Actions</option>
            {uniqueActions.map((a) => (
              <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <input
            data-testid="filter-activity-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lead #, customer, user, employee id, details…"
            className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none text-sm"
          />
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No activities match your filters</div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((a) => (
                <div key={a.id} data-testid={`activity-${a.id}`} className="p-5 hover:bg-accent/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg flex-shrink-0 ${getColor(a.action)}`}>
                      {getIcon(a.entity_type, a.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium text-foreground">{a.details || a.action}</p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(a.created_at)}</span>
                      </div>

                      {/* Lead context */}
                      {(a.lead_number || a.customer_name) && (
                        <div className="mt-1 text-sm text-foreground/80">
                          {a.lead_number && (
                            <span className="font-mono text-xs px-2 py-0.5 bg-muted rounded mr-2">{a.lead_number}</span>
                          )}
                          {a.customer_name && <span>{a.customer_name}</span>}
                        </div>
                      )}

                      {/* Status transition */}
                      {(a.previous_status || a.new_status) && (
                        <div className="mt-1 flex items-center gap-2 text-xs">
                          {a.previous_status && (
                            <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 capitalize">{a.previous_status}</span>
                          )}
                          <ArrowRight size={12} className="text-muted-foreground" />
                          {a.new_status && (
                            <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize">{a.new_status}</span>
                          )}
                        </div>
                      )}

                      {/* Remarks */}
                      {a.remarks && (
                        <div className="mt-2 text-sm italic text-muted-foreground border-l-2 border-border pl-3">
                          "{a.remarks}"
                        </div>
                      )}

                      {/* Actor + entity */}
                      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span className="capitalize">{a.entity_type} · {a.action.replace(/_/g, ' ')}</span>
                        <span className="text-foreground/70">
                          by <span className="font-medium text-foreground">{a.actor_name || 'System'}</span>
                          {a.actor_role && <span> ({formatRole(a.actor_role)})</span>}
                          {a.actor_employee_id && <span className="ml-1 font-mono">#{a.actor_employee_id}</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Activities;
